import { useEffect, useMemo, useState } from 'react';

import {
  CheckCircle2,
  ChevronDown,
  CircleX,
  Info,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';

import { Button } from 'components/Button';
import { Card } from 'components/Card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { Progress } from 'components/ProgressBar';
import { Flex } from 'components/layout/Flex';
import { Typography } from 'components/typography';
import {
  CloneCreateResponse,
  CloneJobPhase,
  CloneJobStatus,
} from 'lib/api/cloneLoads';
import { cn } from 'utils/shadcn';

import JobDetails from './JobDetails';
import {
  activeDescription,
  activeHeading,
  allRowsAreDuplicates,
  formatCompletedAt,
  jobHasIssues,
  onlyDuplicateSkips,
  terminalHeading,
  terminalSummary,
} from './utils';

type JobCardProps = {
  job: CloneCreateResponse;
  refreshError?: string;
  onRefresh: () => void;
  onOpenLoad: (loadIdentifier: string) => void;
};

export default function JobCard({
  job,
  refreshError,
  onRefresh,
  onOpenLoad,
}: JobCardProps) {
  const isComplete = job.phase === CloneJobPhase.Complete;
  const hasIssues = jobHasIssues(job);
  const hasDetails =
    job.items.length > 0 ||
    job.extractionWarnings.length > 0 ||
    job.status === CloneJobStatus.Failed;
  const [expanded, setExpanded] = useState(isComplete && hasIssues);

  useEffect(() => {
    setExpanded(false);
  }, [job.jobId]);

  useEffect(() => {
    if (isComplete) {
      setExpanded(hasIssues);
    }
  }, [hasIssues, isComplete, job.jobId]);

  const heading = isComplete ? terminalHeading(job) : activeHeading(job);
  const description = isComplete
    ? terminalSummary(job)
    : activeDescription(job);
  const progressValue = useMemo(() => {
    if (
      job.phase !== CloneJobPhase.Creating ||
      job.progress.total === null ||
      job.progress.total <= 0
    ) {
      return null;
    }
    return Math.min(
      100,
      Math.max(0, (job.progress.completed / job.progress.total) * 100)
    );
  }, [job.phase, job.progress.completed, job.progress.total]);

  const header = (
    <Flex className='w-full gap-2.5 text-left'>
      <JobStatusIcon job={job} />
      <div
        className='min-w-0 flex-1'
        role='status'
        aria-live='polite'
        aria-atomic='true'
      >
        <Typography variant='body-sm' weight='medium' className='leading-5'>
          {heading}
        </Typography>
        <Typography
          variant='body-xs'
          textColor='lessMuted'
          className='mt-0.5 leading-4'
        >
          {description}
        </Typography>
        {isComplete && job.completedAt && (
          <Typography
            variant='caption'
            className='mt-0.5 text-[11px] leading-4'
          >
            {formatCompletedAt(job.completedAt)}
          </Typography>
        )}
      </div>
      {hasDetails && (
        <ChevronDown
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200 motion-reduce:transition-none',
            expanded && 'rotate-180'
          )}
          aria-hidden='true'
        />
      )}
    </Flex>
  );

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <Card className='overflow-hidden bg-neutral-50 shadow-none'>
        {hasDetails ? (
          <CollapsibleTrigger
            className='w-full cursor-pointer p-3 outline-none transition-colors hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-brand-main focus-visible:ring-inset'
            aria-label={`${expanded ? 'Hide' : 'Show'} load cloning details: ${heading}, ${description}`}
          >
            {header}
          </CollapsibleTrigger>
        ) : (
          <div className='p-3'>{header}</div>
        )}

        {!isComplete && progressValue !== null && (
          <div className='px-3 pb-3'>
            <Progress
              value={progressValue}
              max={100}
              className='h-1.5 bg-neutral-200'
              indicatorClassName='bg-brand-main'
              aria-label='Load creation progress'
              aria-valuetext={`${job.progress.completed} of ${job.progress.total} complete`}
            />
          </div>
        )}

        {refreshError && (
          <div
            className='mx-3 mb-3 rounded-md border border-warning-200 bg-warning-50 p-2.5'
            role='alert'
          >
            <Typography
              variant='body-xs'
              className='leading-4 text-warning-800'
            >
              Status could not be refreshed. The job may still be running.
            </Typography>
            <Button
              buttonNamePosthog={null}
              variant='ghost'
              size='auto'
              className='mt-1.5 h-auto gap-1.5 px-0 py-1 text-xs text-warning-800 hover:border-transparent'
              onClick={onRefresh}
            >
              <RefreshCw className='h-3.5 w-3.5' aria-hidden='true' />
              Refresh status
            </Button>
          </div>
        )}

        <CollapsibleContent>
          <JobDetails job={job} onOpenLoad={onOpenLoad} />
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function JobStatusIcon({ job }: { job: CloneCreateResponse }) {
  if (job.phase !== CloneJobPhase.Complete) {
    return (
      <Loader2
        className='mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand-main motion-reduce:animate-none'
        aria-hidden='true'
        data-testid='clone-job-status-active'
      />
    );
  }

  if (job.status === CloneJobStatus.Failed) {
    return (
      <CircleX
        className='mt-0.5 h-4 w-4 shrink-0 text-error-600'
        aria-hidden='true'
        data-testid='clone-job-status-error'
      />
    );
  }
  if (
    allRowsAreDuplicates(job) ||
    (onlyDuplicateSkips(job) && job.extractionWarnings.length === 0)
  ) {
    return (
      <Info
        className='mt-0.5 h-4 w-4 shrink-0 text-neutral-600'
        aria-hidden='true'
        data-testid='clone-job-status-info'
      />
    );
  }
  if (jobHasIssues(job)) {
    return (
      <TriangleAlert
        className='mt-0.5 h-4 w-4 shrink-0 text-warning-600'
        aria-hidden='true'
        data-testid='clone-job-status-warning'
      />
    );
  }
  return (
    <CheckCircle2
      className='mt-0.5 h-4 w-4 shrink-0 text-success-600'
      aria-hidden='true'
      data-testid='clone-job-status-success'
    />
  );
}
